import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import moment from "moment";
import { fetchRoute, getCachedRoute, ROUTING_MODES, DEFAULT_ROUTING_MODE } from "./routing";

// Centre par défaut (Lyon) tant qu'aucune tournée n'est affichée
const DEFAULT_CENTER = [45.7578, 4.8320];
const DEFAULT_ZOOM = 12;

// Points de départ des tournées (dépôts des établissements)
export const DEPOTS = {
  L: { lat: 45.74552, lng: 4.86504, name: "Lumière", address: "49 avenue des Frères Lumière, 69008 Lyon" },
  V: { lat: 45.775588, lng: 4.803669, name: "Valmy", address: "40 rue Marietton, 69009 Lyon" },
};

export const getDepotForTour = (tour) => {
  const initial = tour.establishment && tour.establishment.initial;
  if (initial && DEPOTS[initial]) {
    return DEPOTS[initial];
  }
  const name = ((tour.establishment && tour.establishment.name) || "").toLowerCase();
  if (name.includes("lumière") || name.includes("lumiere")) {
    return DEPOTS.L;
  }
  if (name.includes("marietton") || name.includes("valmy")) {
    return DEPOTS.V;
  }
  return null;
};

/**
 * Carte Leaflet affichant les tournées sélectionnées :
 * - une polyline par tournée (dans l'ordre de livraison)
 * - un marqueur numéroté par point, cliquable pour (dé)sélectionner
 */
const TourMap = (props) => {

  const { tours, selectedSpotIds, highlightedSpotIds, onToggleSpot, addressMarker, addressEstimate, centerOn, bikeRoute, bikeTargetSpotId, estimatedPosition, routingMode } = props;

  const containerRef    = useRef(null);
  const mapRef          = useRef(null);
  const layerRef        = useRef(null);
  const addressLayerRef   = useRef(null);
  const bikeLayerRef      = useRef(null);
  const estimateLayerRef  = useRef(null);
  const lastFitKey        = useRef(null);
  const lastAddressKey    = useRef(null);
  const toggleRef       = useRef(onToggleSpot);
  toggleRef.current     = onToggleSpot;
  const drawGenRef      = useRef(0);
  // Pictos du mode de déplacement courant (vélo/voiture)
  const modeIcons = ROUTING_MODES[routingMode] || ROUTING_MODES[DEFAULT_ROUTING_MODE];

  // Initialisation de la carte (une seule fois)
  useEffect(() => {
    const map = L.map(containerRef.current, {
      scrollWheelZoom: true,
    }).setView(DEFAULT_CENTER, DEFAULT_ZOOM);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    layerRef.current = L.layerGroup().addTo(map);
    addressLayerRef.current = L.layerGroup().addTo(map);
    bikeLayerRef.current = L.layerGroup().addTo(map);
    estimateLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    // La carte peut être montée dans un onglet caché : recalcule la taille
    const invalidate = () => map.invalidateSize();
    const timer = setTimeout(invalidate, 250);
    window.addEventListener("resize", invalidate);

    let observer = null;
    if (window.ResizeObserver) {
      observer = new ResizeObserver(invalidate);
      observer.observe(containerRef.current);
    }

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", invalidate);
      if (observer) {
        observer.disconnect();
      }
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // (Re)dessine tournées et marqueurs
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) {
      return;
    }

    layer.clearLayers();
    const bounds = [];
    const gen = ++drawGenRef.current;
    // Dépôts de départ à dessiner (une seule fois même si plusieurs tournées)
    const depotsToDraw = {};

    tours.forEach((tour) => {
      const points = (tour.itinerary || []).filter((point) =>
        point.spot_coordinates && point.spot_coordinates.lat && point.spot_coordinates.lng
      );

      let latlngs = points.map((point) => [point.spot_coordinates.lat, point.spot_coordinates.lng]);

      // La tournée part du dépôt de son établissement (Valmy ou Lumière)
      const depot = getDepotForTour(tour);
      if (depot && latlngs.length > 0) {
        latlngs = [[depot.lat, depot.lng], ...latlngs];
        depotsToDraw[depot.name] = depot;
      }

      latlngs.forEach((latlng) => bounds.push(latlng));

      if (latlngs.length > 1) {
        const cachedRoute = getCachedRoute(latlngs, routingMode);

        if (cachedRoute) {
          // Itinéraire déjà calculé pour ce mode
          L.polyline(cachedRoute.latlngs, {
            color: tour.displayColor,
            weight: 4,
            opacity: 0.8,
          }).addTo(layer);
        } else if (cachedRoute === null) {
          // Routage indisponible pour ce tracé : ligne droite
          L.polyline(latlngs, {
            color: tour.displayColor,
            weight: 3,
            opacity: 0.7,
            dashArray: "6 8",
          }).addTo(layer);
        } else {
          // Ligne droite provisoire le temps de calculer l'itinéraire
          const placeholder = L.polyline(latlngs, {
            color: tour.displayColor,
            weight: 3,
            opacity: 0.5,
            dashArray: "6 8",
          }).addTo(layer);

          fetchRoute(latlngs, routingMode).then((route) => {
            // Ignore si la carte a été redessinée entre-temps (le cache resservira)
            if (!route || drawGenRef.current !== gen || !layer.hasLayer(placeholder)) {
              return;
            }
            layer.removeLayer(placeholder);
            L.polyline(route.latlngs, {
              color: tour.displayColor,
              weight: 4,
              opacity: 0.8,
            }).addTo(layer);
          });
        }
      }

      points.forEach((point, index) => {
        const isSelected = selectedSpotIds.includes(point.spot_id);
        const isHighlighted = highlightedSpotIds.includes(point.spot_id);
        // Point cible de l'estimation vélo : couleur turquoise distincte
        const isBikeTarget = bikeTargetSpotId === point.spot_id;
        // Code complet du point (ex "V 4.1CNR"), ordre de passage en secours
        const number = point.spot_number || index + 1;

        let markerShadow = "0 1px 4px rgba(0,0,0,0.4)";
        if (isBikeTarget) {
          markerShadow = "0 0 0 5px rgba(6, 182, 212, 0.45), 0 1px 4px rgba(0,0,0,0.4)";
        } else if (isHighlighted) {
          markerShadow = "0 0 0 5px rgba(255, 193, 7, 0.9), 0 1px 4px rgba(0,0,0,0.4)";
        }

        // Coche verte sur les points marqués livrés (Intendance / app livraison)
        const deliveredBadge = point.delivered
          ? `<div style="
              position: absolute; top: -8px; right: -8px;
              width: 16px; height: 16px; border-radius: 50%;
              background: #059669; border: 2px solid #ffffff;
              color: #ffffff; font-size: 10px; line-height: 12px;
              text-align: center; font-weight: bold;
            " role="img" aria-label="livré">✓</div>`
          : "";

        const icon = L.divIcon({
          className: "",
          html: `
            <div style="
              position: relative;
              transform: translate(-50%, -50%)${isSelected || isHighlighted || isBikeTarget ? " scale(1.15)" : ""};
              display: inline-flex; align-items: center; justify-content: center;
              min-width: 30px; height: 26px; padding: 0 8px;
              border-radius: 13px; white-space: nowrap;
              background: ${isBikeTarget ? "#06b6d4" : tour.displayColor};
              border: ${isSelected ? "3px solid #212529" : "2px solid #ffffff"};
              box-shadow: ${markerShadow};
              color: ${isBikeTarget ? "#ffffff" : (tour.color_text || "#ffffff")};
              font-size: 12px; font-weight: bold;
              opacity: ${point.delivered ? 0.75 : 1};
            ">${number}${deliveredBadge}</div>`,
          iconSize: null,
        });

        const marker = L.marker([point.spot_coordinates.lat, point.spot_coordinates.lng], {
          icon,
          zIndexOffset: isHighlighted ? 500 : 0,
        }).addTo(layer);

        const deliveryTime = point.delivery_time ? moment(point.delivery_time).format("HH:mm") : null;
        const departureTime = point.departure_time ? moment(point.departure_time).format("HH:mm") : null;
        const timeRange = deliveryTime ? (departureTime ? `${deliveryTime} - ${departureTime}` : deliveryTime) : "";
        marker.bindTooltip(
          `<strong>${point.spot_name || ""}</strong><br/>` +
          `${tour.name || ""}${timeRange ? " - " + timeRange : ""}<br/>` +
          `${point.spot_address || ""}<br/>` +
          (point.delivered
            ? `<span style="color: #059669; font-weight: bold;">✓ Livré${point.delivered_at ? ` à ${moment(point.delivered_at).format("HH:mm")}` : ""}</span><br/>`
            : "") +
          `<em>${isSelected ? "Cliquer pour désélectionner" : "Cliquer pour sélectionner"}</em>`,
          { direction: "top", offset: [0, -18] }
        );

        marker.on("click", () => {
          toggleRef.current(point, tour);
        });
      });
    });

    // Marqueurs des dépôts de départ (icône distincte des points de retrait)
    Object.keys(depotsToDraw).forEach((key) => {
      const depot = depotsToDraw[key];
      const icon = L.divIcon({
        className: "",
        html: `
          <div style="
            transform: translate(-50%, -50%);
            display: flex; align-items: center; justify-content: center;
            width: 36px; height: 36px; border-radius: 8px;
            background: #111827; color: #ffffff;
            font-weight: bold; font-size: 16px;
            border: 2px solid #ffffff;
            box-shadow: 0 2px 6px rgba(0,0,0,0.5);
          " role="img" aria-label="départ ${depot.name}">${depot.name.charAt(0)}</div>`,
        iconSize: null,
      });
      const marker = L.marker([depot.lat, depot.lng], { icon, zIndexOffset: 800 }).addTo(layer);
      marker.bindTooltip(
        `<strong>Départ ${depot.name}</strong><br/>${depot.address}`,
        { direction: "top", offset: [0, -22] }
      );
    });

    // Ne recadre que si la liste des tournées ou la date change (pas à chaque sélection)
    const fitKey = tours.map((tour) => tour.id).join(",");
    if (bounds.length > 0 && fitKey !== lastFitKey.current) {
      map.fitBounds(bounds, { padding: [40, 40] });
    }
    lastFitKey.current = fitKey;
  }, [tours, selectedSpotIds, highlightedSpotIds, bikeTargetSpotId, routingMode]);

  // Itinéraire vélo estimé (position livreur -> point choisi)
  useEffect(() => {
    const map = mapRef.current;
    const layer = bikeLayerRef.current;
    if (!map || !layer) {
      return;
    }

    layer.clearLayers();

    if (!bikeRoute || bikeRoute.length < 2) {
      return;
    }

    const polyline = L.polyline(bikeRoute, {
      color: "#17a2b8",
      weight: 5,
      opacity: 0.9,
      dashArray: "2 8",
    }).addTo(layer);

    map.fitBounds(polyline.getBounds(), { padding: [40, 40] });
  }, [bikeRoute]);

  // Position théorique estimée du livreur sur sa tournée
  useEffect(() => {
    const map = mapRef.current;
    const layer = estimateLayerRef.current;
    if (!map || !layer) {
      return;
    }

    layer.clearLayers();

    if (!estimatedPosition) {
      return;
    }

    const icon = L.divIcon({
      className: "",
      html: `
        <div style="font-size: 22px; line-height: 36px; text-align: center;
                    width: 40px; height: 40px; border-radius: 50%;
                    background: #ffffff; border: 3px solid ${estimatedPosition.color || "#212529"};
                    box-shadow: 0 2px 6px rgba(0,0,0,0.4);"
             role="img" aria-label="position estimée du livreur">${modeIcons.riderEmoji}</div>`,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
    });

    const marker = L.marker([estimatedPosition.lat, estimatedPosition.lng], {
      icon,
      zIndexOffset: 900,
    }).addTo(layer);
    marker.bindTooltip(
      `<strong>Position estimée</strong><br/>${estimatedPosition.label || ""}<br/><em>D'après les horaires prévus</em>`,
      { direction: "top", offset: [0, -22], permanent: true }
    );

    map.setView([estimatedPosition.lat, estimatedPosition.lng], Math.max(map.getZoom(), 14));
  }, [estimatedPosition, modeIcons]);

  // Centrage demandé (ex : "Voir sur la carte" depuis la recherche client)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !centerOn || !centerOn.lat) {
      return;
    }
    map.setView([centerOn.lat, centerOn.lng], Math.max(map.getZoom(), 15));
  }, [centerOn]);

  // Marqueur d'adresse recherchée (localisation d'un client)
  useEffect(() => {
    const map = mapRef.current;
    const layer = addressLayerRef.current;
    if (!map || !layer) {
      return;
    }

    layer.clearLayers();

    if (!addressMarker) {
      lastAddressKey.current = null;
      return;
    }

    const icon = L.divIcon({
      className: "",
      html: `
        <div style="font-size: 28px; line-height: 32px; text-align: center;
                    width: 40px; height: 40px; border-radius: 50%;
                    background: #ffffff; border: 2px solid #212529;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.4);"
             role="img" aria-label="localisation livreur">${modeIcons.emoji}</div>`,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
    });

    const marker = L.marker([addressMarker.lat, addressMarker.lng], { icon, zIndexOffset: 1000 }).addTo(layer);
    marker.bindTooltip(
      `<strong>${addressMarker.label || "Adresse recherchée"}</strong>` +
      (addressEstimate
        ? `<br/><span style="font-weight: normal;">${modeIcons.emoji} ${addressEstimate}</span>`
        : ""),
      {
        direction: "top",
        offset: [0, -22],
        permanent: true,
      }
    );

    // Ne recentre que si l'adresse a changé (pas quand seule l'estimation change)
    const addressKey = `${addressMarker.lat},${addressMarker.lng}`;
    if (lastAddressKey.current !== addressKey) {
      map.setView([addressMarker.lat, addressMarker.lng], Math.max(map.getZoom(), 14));
      lastAddressKey.current = addressKey;
    }
  }, [addressMarker, addressEstimate, modeIcons]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "600px",
        borderRadius: "12px",
        border: "1px solid #e5e7eb",
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
        zIndex: 0,
      }}
    />
  );
};

TourMap.propTypes = {
  tours: PropTypes.array,
  selectedSpotIds: PropTypes.array,
  highlightedSpotIds: PropTypes.array,
  onToggleSpot: PropTypes.func.isRequired,
  addressMarker: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
    label: PropTypes.string,
  }),
  addressEstimate: PropTypes.string,
  bikeTargetSpotId: PropTypes.number,
  estimatedPosition: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
    label: PropTypes.string,
    color: PropTypes.string,
  }),
  centerOn: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  bikeRoute: PropTypes.array,
  routingMode: PropTypes.string,
};

TourMap.defaultProps = {
  tours: [],
  selectedSpotIds: [],
  highlightedSpotIds: [],
  addressMarker: null,
  addressEstimate: null,
  centerOn: null,
  bikeRoute: null,
  bikeTargetSpotId: null,
  estimatedPosition: null,
  routingMode: DEFAULT_ROUTING_MODE,
};

export default TourMap;
